import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  Chips,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pill,
  RefreshButton,
  SkeletonRows,
  SkeletonStats,
  Stat,
  Tone,
} from "../ui";
import { useCleanerAttendance } from "./useCleanerAttendance";
import { CleanerEventRow } from "./cleanerAttendanceTypes";
import {
  DUE_AFTER_MS,
  StoreAttendanceStatus,
  cleanerName,
  compareStoresByUrgency,
  eventLabel,
  formatAbsolute,
  formatAgo,
  formatDistance,
  formatSessionDuration,
  formatTime,
  googleMapsUrl,
  lastAttendedMs,
  statusLabel,
  statusTone,
  storeAttendanceStatus,
} from "./cleanerAttendanceUtils";

type StatusFilter = "all" | StoreAttendanceStatus;

const FILTER_CHIPS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "onsite", label: "On site" },
  { id: "due", label: "Due" },
  { id: "overdue", label: "Overdue" },
  { id: "never", label: "Never" },
];

function StatusPill({ status }: { status: StoreAttendanceStatus }) {
  return <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>;
}

function SiteBadge({ event }: { event: CleanerEventRow }) {
  const distance = formatDistance(event.distanceMetres);
  if (event.onSite == null) {
    return <Pill>No GPS</Pill>;
  }
  return (
    <Pill tone={event.offSite ? "warn" : "good"}>
      {event.offSite ? "Off-site" : "On-site"}
      {distance ? ` · ${distance}` : ""}
    </Pill>
  );
}

function StoreEventsTable({ events }: { events: CleanerEventRow[] }) {
  if (events.length === 0) {
    return <EmptyState compact title="No clock-ins recorded for this store yet" />;
  }
  return (
    <table className="sq-table is-stacked">
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Event</th>
          <th scope="col">Cleaner</th>
          <th scope="col">Location</th>
          <th scope="col">Matched by</th>
        </tr>
      </thead>
      <tbody>
        {events.map((e) => (
          <tr key={e.eventId}>
            <td data-label="Time" title={formatAbsolute(e.occurredAt)}>
              <div className="sq-strong">{formatTime(e.occurredAt)}</div>
              <div className="sq-muted">{formatAgo(e.occurredAt)}</div>
            </td>
            <td data-label="Event">{eventLabel(e.event)}</td>
            <td data-label="Cleaner">{cleanerName(e.cleaner)}</td>
            <td data-label="Location">
              <SiteBadge event={e} />
              {!e.countsAsAttendance ? <div className="sq-muted mt-1">Not counted</div> : null}
            </td>
            <td data-label="Matched by">{e.matchMethod ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function CleanersPage() {
  const [searchParams] = useSearchParams();
  const focusLocationId = Number(searchParams.get("locationId")) || null;
  const { data, loading, error, refreshing, reload } = useCleanerAttendance();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const [showUnassigned, setShowUnassigned] = useState(false);
  const focusedRef = useRef<number | null>(null);

  const now = (data && Date.parse(data.generatedAt)) || Date.now();

  const storesWithStatus = useMemo(() => {
    if (!data) return [];
    return [...data.stores]
      .sort((a, b) => compareStoresByUrgency(a, b, now))
      .map((row) => ({ row, status: storeAttendanceStatus(row, now) }));
  }, [data, now]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: storesWithStatus.length,
      onsite: 0,
      ok: 0,
      due: 0,
      overdue: 0,
      never: 0,
    };
    storesWithStatus.forEach(({ status }) => {
      c[status] += 1;
    });
    return c;
  }, [storesWithStatus]);

  const attendedLast24h = useMemo(
    () =>
      storesWithStatus.filter(({ row }) => {
        if (row.cleanerOnSiteNow) return true;
        const ms = lastAttendedMs(row);
        return ms != null && now - ms <= DUE_AFTER_MS;
      }).length,
    [storesWithStatus, now]
  );

  const visibleStores = useMemo(() => {
    const q = search.trim().toLowerCase();
    return storesWithStatus.filter(({ row, status }) => {
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q) ||
        (row.locationPin ?? "").toLowerCase().includes(q)
      );
    });
  }, [storesWithStatus, search, statusFilter]);

  useEffect(() => {
    if (!data || focusLocationId == null || focusedRef.current === focusLocationId) return;
    if (!data.stores.some((s) => s.locationId === focusLocationId)) return;
    focusedRef.current = focusLocationId;
    setExpanded((prev) => new Set(prev).add(focusLocationId));
    window.requestAnimationFrame(() => {
      document
        .getElementById(`cleaner-store-${focusLocationId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [data, focusLocationId]);

  const toggleExpanded = (locationId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  const dueOverdueTone: Tone | undefined =
    counts.overdue > 0 ? "bad" : counts.due > 0 ? "warn" : undefined;

  return (
    <PageContainer>
      <PageHeader
        title="Cleaners"
        subtitle={
          data?.generatedAt
            ? `Updated ${formatAbsolute(data.generatedAt)}${refreshing ? " · refreshing…" : ""}`
            : undefined
        }
        actions={
          <RefreshButton onClick={() => reload({ soft: true })} loading={loading || refreshing} />
        }
      />

      {error ? <ErrorBanner onRetry={() => reload()}>{error}</ErrorBanner> : null}

      {loading && !data ? (
        <div className="sq-stack" role="status" aria-label="Loading cleaner attendance">
          <SkeletonStats />
          <Card>
            <SkeletonRows rows={6} />
          </Card>
        </div>
      ) : null}

      {data ? (
        <div className="sq-stack">
          <section className="sq-stats" aria-label="Attendance totals">
            <Stat label="Stores" value={counts.all} />
            <Stat label="On site now" value={counts.onsite} />
            <Stat label="Attended in last 24h" value={attendedLast24h} />
            <Stat
              icon={dueOverdueTone ? <IconAlertTriangle size={16} /> : undefined}
              label="Due / overdue"
              tone={dueOverdueTone}
              value={`${counts.due} / ${counts.overdue}`}
            />
          </section>

          <Card
            flush
            title="Stores"
            subtitle="Last on-site visit per store. Due after 24h, overdue after 48h. Off-site clock-ins are shown but never count as a visit."
            aria-label="Stores"
          >
            <div className="sq-toolbar">
              <label className="sq-field">
                <span>Search</span>
                <input
                  type="search"
                  className="sq-input"
                  placeholder="Store name or pin"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
              <Chips
                label="Filter by attendance status"
                value={statusFilter}
                onChange={setStatusFilter}
                options={FILTER_CHIPS.map((chip) => ({
                  id: chip.id,
                  label: `${chip.label} (${counts[chip.id]})`,
                }))}
              />
            </div>

            {visibleStores.length === 0 ? (
              <EmptyState title="No stores match the current filters" />
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col">Store</th>
                      <th scope="col">Status</th>
                      <th scope="col">Last attended</th>
                      <th scope="col">On site now</th>
                      <th scope="col">Recent</th>
                      <th scope="col" aria-label="Expand" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStores.map(({ row, status }) => {
                      const isOpen = expanded.has(row.locationId);
                      const offSiteCount = row.events.filter((e) => e.offSite).length;
                      const duration = row.lastAttended
                        ? formatSessionDuration(row.lastAttended.clockIn, row.lastAttended.clockOut)
                        : null;
                      return (
                        <Fragment key={row.locationId}>
                          <tr
                            id={`cleaner-store-${row.locationId}`}
                            className={`is-clickable${
                              focusLocationId === row.locationId ? " is-flagged" : ""
                            }`}
                            onClick={() => toggleExpanded(row.locationId)}
                          >
                            <td className="cell-title">
                              {row.layoutSiteId ? (
                                <Link
                                  to={`/status?siteId=${encodeURIComponent(row.layoutSiteId)}`}
                                  className="sq-text-link"
                                  title={`Open status for ${row.name}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row.name}
                                </Link>
                              ) : (
                                <span className="sq-strong">{row.name}</span>
                              )}
                              {row.locationPin ? (
                                <div className="sq-muted">{row.locationPin}</div>
                              ) : null}
                            </td>
                            <td data-label="Status">
                              <StatusPill status={status} />
                            </td>
                            <td
                              data-label="Last attended"
                              title={formatAbsolute(row.lastAttended?.clockOut)}
                            >
                              {row.lastAttended ? (
                                <>
                                  <div className="sq-strong">
                                    {formatAgo(row.lastAttended.clockOut)}
                                  </div>
                                  <div className="sq-muted">
                                    {cleanerName(row.lastAttended.cleaner)}
                                    {duration ? ` · ${duration}` : ""}
                                  </div>
                                </>
                              ) : (
                                <span className="sq-muted">No visits recorded</span>
                              )}
                            </td>
                            <td
                              data-label="On site now"
                              title={formatAbsolute(row.cleanerOnSiteNow?.clockIn)}
                            >
                              {row.cleanerOnSiteNow ? (
                                <>
                                  <div className="sq-strong">
                                    {cleanerName(row.cleanerOnSiteNow.cleaner)}
                                  </div>
                                  <div className="sq-muted">
                                    since {formatTime(row.cleanerOnSiteNow.clockIn)}
                                  </div>
                                </>
                              ) : (
                                <span className="sq-muted">—</span>
                              )}
                            </td>
                            <td data-label="Recent">
                              <span>{row.events.length}</span>
                              {offSiteCount > 0 ? (
                                <span className="ms-2">
                                  <Pill
                                    tone="warn"
                                    icon={<IconAlertTriangle size={12} />}
                                    title={`${offSiteCount} recent event(s) clocked off-site`}
                                  >
                                    {offSiteCount} off-site
                                  </Pill>
                                </span>
                              ) : null}
                            </td>
                            <td className="cell-actions">
                              <button
                                type="button"
                                className="sq-row-toggle"
                                aria-expanded={isOpen}
                                aria-label={`${isOpen ? "Hide" : "Show"} recent events for ${row.name}`}
                              >
                                {isOpen ? (
                                  <IconChevronDown size={18} />
                                ) : (
                                  <IconChevronRight size={18} />
                                )}
                              </button>
                            </td>
                          </tr>
                          {isOpen ? (
                            <tr className="is-detail">
                              <td colSpan={6}>
                                <StoreEventsTable events={row.events} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card
            flush
            title={
              <button
                type="button"
                className="sq-card-toggle"
                onClick={() => setShowUnassigned((v) => !v)}
                aria-expanded={showUnassigned}
              >
                {showUnassigned ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                Unassigned events ({data.unassigned.length})
              </button>
            }
            subtitle={
              showUnassigned
                ? "Clock-ins that couldn't be matched to one of our stores, either by the store name in the HR system or by GPS within 200m."
                : undefined
            }
            aria-label="Unassigned events"
          >
            {showUnassigned ? (
              data.unassigned.length === 0 ? (
                <EmptyState compact title="No unassigned events" />
              ) : (
                <div className="sq-table-wrap">
                  <table className="sq-table is-stacked">
                    <thead>
                      <tr>
                        <th scope="col">Time</th>
                        <th scope="col">Event</th>
                        <th scope="col">Cleaner</th>
                        <th scope="col">HR store name</th>
                        <th scope="col">Location</th>
                        <th scope="col">Coordinates</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unassigned.map((e) => (
                        <tr key={e.eventId}>
                          <td data-label="Time" title={formatAbsolute(e.occurredAt)}>
                            <div className="sq-strong">{formatTime(e.occurredAt)}</div>
                            <div className="sq-muted">{formatAgo(e.occurredAt)}</div>
                          </td>
                          <td data-label="Event">{eventLabel(e.event)}</td>
                          <td data-label="Cleaner">{cleanerName(e.cleaner)}</td>
                          <td data-label="HR store name">{e.locationName || "—"}</td>
                          <td data-label="Location">
                            <SiteBadge event={e} />
                          </td>
                          <td data-label="Coordinates">
                            {e.latitude != null && e.longitude != null ? (
                              <a
                                href={googleMapsUrl(e.latitude, e.longitude)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="sq-text-link"
                              >
                                {e.latitude.toFixed(5)}, {e.longitude.toFixed(5)}
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </Card>
        </div>
      ) : null}
    </PageContainer>
  );
}
